"use client";

import { useMemo, useState } from "react";
import type { DownloadPlatform } from "@/lib/data/downloadPlatforms";
import type { PlatformRelease } from "@/lib/data/playerReleases";
import { PlatformCard } from "./PlatformCard";

interface PlatformSearchProps {
  platforms: DownloadPlatform[];
  /** Live releases keyed by platform id. Missing entries hide the block. */
  releases?: Record<string, PlatformRelease>;
  /** CDN base path for the player icons. */
  baseUrl: string;
}

const SearchIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
    <path d="M16.5 16.5L21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const ClearIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

/** Everything on a card that a visitor might reasonably type. */
const searchableText = (platform: DownloadPlatform) =>
  [
    platform.name,
    platform.id,
    platform.description,
    ...platform.downloadItems.flatMap((item) => [item.label, item.value]),
  ]
    .join(" ")
    .toLowerCase();

export function PlatformSearch({ platforms, releases, baseUrl }: PlatformSearchProps) {
  const [query, setQuery] = useState("");

  const haystacks = useMemo(
    () => platforms.map((platform) => ({ platform, text: searchableText(platform) })),
    [platforms]
  );

  const trimmed = query.trim().toLowerCase();
  const matches = trimmed
    ? haystacks.filter(({ text }) => text.includes(trimmed)).map(({ platform }) => platform)
    : platforms;

  return (
    <>
      <div className="mb-8 flex flex-col gap-6 border-b border-gray-200 pb-6 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-xl font-bold leading-tight text-gray-900">
            Download Latest Player Version
          </h2>
          <p className="mt-1 text-[15px] text-gray-600">Choose your player OS and get started.</p>
        </div>

        <div className="relative w-full flex-shrink-0 md:w-[360px]">
          <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
            <SearchIcon />
          </span>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search your player OS..."
            aria-label="Search download platforms"
            className="w-full rounded-xl border border-gray-400 bg-white py-3.5 pl-12 pr-11 text-[15px] text-gray-900 outline-none transition-colors placeholder:text-gray-400 focus:border-primary focus:ring-2 focus:ring-primary/25 [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              aria-label="Clear search"
              className="absolute right-3 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
            >
              <ClearIcon />
            </button>
          )}
        </div>
      </div>

      <p role="status" aria-live="polite" className="sr-only">
        {matches.length} {matches.length === 1 ? "platform" : "platforms"} found
      </p>

      {matches.length > 0 ? (
        <div className="grid grid-cols-1 gap-7 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
          {matches.map((platform, index) => (
            <PlatformCard
              key={platform.id}
              platform={platform}
              release={releases?.[platform.id]}
              index={index}
              baseUrl={baseUrl}
            />
          ))}
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-gray-300 px-6 py-14 text-center">
          <p className="text-[15px] text-gray-600">
            No players match <span className="font-semibold text-gray-900">&ldquo;{query.trim()}&rdquo;</span>
          </p>
          <button
            type="button"
            onClick={() => setQuery("")}
            className="mt-4 text-sm font-semibold text-primary transition-colors hover:text-accent"
          >
            Clear search
          </button>
        </div>
      )}
    </>
  );
}
